// Landing when no customer is routed. Lists the reviewer's customers as cards with
// per-customer progress; also links internal-admins to the admin console.

import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowRight, Inbox } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useMyCustomers } from '../hooks/queries';
import { binderName } from '../api/types';
import type { CustomerReviewer } from '../api/types';
import { roundStatusLabel, verticalLabel } from '../reviewVocab';
import { EmptyState, SkeletonRows, Tag } from '../ui/primitives';
import { ProgressBar } from '../components/ui';
import { chooseCapacity, rememberHomeCapacity } from '../components/capacity';
import { isPracticeRole } from '../reviewVocab';
import { api, http } from '../api/client';
import type { FirmSummary } from '../api/types';
import { firmWords } from '../firm/strings/firm';

/**
 * Flags reviewers who were invited but never completed sign-in (internal-admin only —
 * the field is absent for attorneys).
 *
 * A progress bar cannot distinguish "the reviewer is working through it" from "the
 * reviewer has never been able to log in": both sit at 0%. On Advanced Vitality the
 * second one went unnoticed while the binder waited, because the only place that said
 * so was the user-admin screen and nobody had a reason to open it (BL-146).
 */
function BlockedReviewers({ reviewers }: { reviewers?: CustomerReviewer[] }) {
  // Deduped by email: one PERSON holding two grants on a customer rendered
  // as "a@…, b@… — 2 reviewers have never signed in" (2026-09-01 QA
  // walkthrough). The warning is about people, not grant rows.
  const blocked = [
    ...new Set(
      (reviewers ?? []).filter((r) => r.never_signed_in).map((r) => r.email),
    ),
  ];
  if (blocked.length === 0) return null;
  return (
    <div className="small">
      <Tag variant="faint">
        {blocked.length === 1
          ? 'reviewer has never signed in'
          : `${blocked.length} reviewers have never signed in`}
      </Tag>
      <div className="faint" style={{ marginTop: 4 }}>
        {blocked.join(', ')} — invited, but no password set. Resend the invite from Users.
      </div>
    </div>
  );
}

export function Landing() {
  const [firms, setFirms] = useState<FirmSummary[]>([]);
  const { isInternalAdmin, identity } = useAuth();
  // Firms are read for a person whose own role is attorney (the API refuses any other role with
  // a 403 — RC19 caught a team member in the Reviewer capacity asking and being refused).
  const readsFirms = identity?.role === 'attorney';
  useEffect(() => {
    if (!readsFirms) { setFirms([]); return; }
    void api.myFirms().then(setFirms).catch(() => setFirms([]));
  }, [readsFirms]);
  const { data, isLoading } = useMyCustomers();
  // PMWALK PM-33 / CN-3: promise an email only while the server sends one.
  // Asked only when the queue is empty: that is the one sentence it decides.
  const [emailed, setEmailed] = useState(false);
  const queueEmpty = !isLoading && (data ?? []).length === 0;
  useEffect(() => {
    if (!queueEmpty) return;
    void http.get<{ counsel_emailed_when_binder_ready: boolean }>('/review-settings')
      .then(({ data: settings }) => setEmailed(!!settings.counsel_emailed_when_binder_ready))
      .catch(() => setEmailed(false));
  }, [queueEmpty]);

  const customers = isInternalAdmin
    ? [...new Map((data ?? [])
      .filter((customer) => (customer.capacities ?? [customer.my_role]).includes('attorney'))
      .map((customer) => [customer.customer_id, customer])).values()]
    : (data ?? []);
  // Customers this person holds MORE THAN ONE capacity on — those render as two
  // cards, and only then is it worth labelling which is which.
  const dualRole = new Set(
    customers
      .map((c) => c.customer_id)
      .filter((id, i, all) => all.indexOf(id) !== i),
  );

  return (
    <>
      <div className="cx-page-head">
        <div>
          <p className="cx-eyebrow">Compliance review</p>
          <h1>Your review queue</h1>
        </div>
        <div className="cx-actions">
          {firms.map(f => <Link key={f.firm_id} className="vx-btn vx-btn--secondary vx-btn--sm" to={`/firms/${f.firm_id}`}>{firmWords.firmLink}: {f.name}</Link>)}
          <Link className="vx-btn vx-btn--secondary vx-btn--sm" to="/legislation">
            Changes in the law
          </Link>
          {/* The old "Admin console" link went to /admin, which redirects to /,
              which is this queue again for someone whose chosen capacity is
              Reviewer (COHERE2 P1-17). It now does what it says: switch to
              the Team capacity and open the team's Customers page. */}
          {isInternalAdmin && (
            <Link
              className="vx-btn vx-btn--secondary vx-btn--sm"
              to="/"
              onClick={() => rememberHomeCapacity('team')}
            >
              Open as the team
            </Link>
          )}
        </div>
      </div>

      {isLoading ? (
        <SkeletonRows rows={3} />
      ) : customers.length === 0 ? (
        <EmptyState icon={<Inbox size={28} aria-hidden="true" />} title="No customers are assigned to you yet">
          {isInternalAdmin
            ? 'Add a practice from Customers. You can upload its binder from the Account page.'
            : emailed
              ? 'A binder will appear here when the team sends one for review. We’ll email you too.'
              : 'A binder will appear here when the team sends one for review. Check back here.'}
        </EmptyState>
      ) : (
        <div className="cx-queue">
          {customers.map((c) => {
            const name = binderName(c.binder_label, c.snapshot_version);
            const to = isInternalAdmin || !isPracticeRole(c.my_role)
              ? `/c/${c.customer_id}` : `/portal/${c.customer_id}`;
            return (
              <div key={`${c.customer_id}:${c.my_role}`} className="cx-card cx-queue-card">
                <Link to={to} onClick={() => { if (isInternalAdmin) chooseCapacity(c.customer_id, 'attorney'); }} className="cx-queue-card__link vx-card-link">
                  <div className="cx-queue-card__row">
                    <span className="cx-queue-card__name">{c.name}</span>
                    <Tag variant="faint">{verticalLabel(c.vertical)}</Tag>
                  </div>
                  {/* One person may hold two grants on one customer (e.g. counsel
                      who is also the practice owner), which surfaces as two cards.
                      Name the capacity so they are not mistaken for duplicates. */}
                  {dualRole.has(c.customer_id) && (
                    <div style={{ marginTop: 6 }}>
                      <Tag>
                        {isPracticeRole(c.my_role) ? 'as the practice' : 'as reviewing counsel'}
                      </Tag>
                    </div>
                  )}
                  <div className="hstack" style={{ gap: 6, margin: '10px 0 8px' }}>
                    {name ? <Tag variant="mono">Binder {name}</Tag> : <Tag variant="faint">No snapshot yet</Tag>}
                    {c.session_status && <Tag>{roundStatusLabel(c.session_status, c.completion_outcome, c.completion_undecided_count, null, null, c.progress)}</Tag>}
                  </div>
                  <ProgressBar pct={c.progress.pct} slim label={`${c.name}: review progress`} />
                  <div className="small muted num" style={{ marginTop: 6 }}>
                    {c.progress.reviewed} of {c.progress.total} decided ·{' '}
                    {Math.round(c.progress.pct)}%
                  </div>
                </Link>
                <BlockedReviewers reviewers={c.reviewers} />
                <div className="cx-queue-card__foot">
                  <Link to={to} onClick={() => { if (isInternalAdmin) chooseCapacity(c.customer_id, 'attorney'); }} className="vx-btn vx-btn--secondary vx-btn--sm" style={{ textDecoration: 'none' }}>
                    {isInternalAdmin || !isPracticeRole(c.my_role) ? 'Open the review' : 'Open the practice'}
                    <ArrowRight size={14} aria-hidden="true" />
                  </Link>
                  {/* An admin's card always links to the REVIEW surface, because
                      their grant is internal_admin on every customer — so without
                      this there is no route from the UI to the customer's own view
                      at all, even though the backend admits admins to it. Both
                      verticals now have one (BL-205); the route is the same and
                      the shell dispatches on the customer's vertical. */}
                  {/* Reachable whether or not a binder exists: the notice
                      surface needs only the grant. Shown for counsel and the
                      team, never for a practice-only grant -- those are
                      internal drafts and the server refuses them anyway. */}
                  {/* UXWALK P2-11: the page head carries "Changes in the law";
                      each card names its own practice's changes instead of
                      repeating the heading. */}
                  {!isPracticeRole(c.my_role) && (
                    <Link to={`/c/${c.customer_id}/notices`} className="small">
                      This practice's changes in the law
                      {(c.law_notices_waiting ?? 0) > 0 && (
                        <> · {c.law_notices_waiting === 1 ? '1 note waiting for you' : `${c.law_notices_waiting} notes waiting for you`}</>
                      )}
                    </Link>
                  )}
                  {isInternalAdmin && (
                    <Link to={`/portal/${c.customer_id}`} className="small">
                      Open the practice (preview)
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
